import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SplashCursor from './SplashCursor';
import { FaWhatsapp, FaTelegramPlane, FaInstagram, FaFacebookF, FaYoutube } from 'react-icons/fa';
import './Terms.css';
import './MainPage.css'; // to inherit footer and header styles
import Footer from './Footer';

const Terms = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="terms-page">
      <SplashCursor RAINBOW_MODE={false} COLOR="#A855F7" />

      {/* Navigation */}
      <header className="header">
        <div className="header-container">
          <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/images/Final Font Stocks4U1.png" alt="Stocks4U Logo" className="logo-text" style={{ width: '150px', height: 'auto' }} />
          </div>
          
          <nav className={`nav ${isMobileMenuOpen ? 'nav-open' : ''}`}>
            <a href="/" onClick={() => setIsMobileMenuOpen(false)}>Home</a>
            <a href="/#features" onClick={() => setIsMobileMenuOpen(false)}>Features</a>
            <a href="/#pricing" onClick={() => setIsMobileMenuOpen(false)}>Pricing</a>
            <a href="/about" onClick={() => setIsMobileMenuOpen(false)}>About</a>
            <a href="/#contact" onClick={() => setIsMobileMenuOpen(false)}>Contact</a>
          </nav>

          <button
            className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          
          <div className="header-buttons">
            <button className="login-btn" onClick={() => navigate('/login')}>Login</button>
            <button className="get-started-btn" onClick={() => navigate('/login')}>Get Started</button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="terms-hero-new">
        <div className="terms-hero-bg-glow"></div>
        <div className="terms-hero-content">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="terms-title-hero"
          >
            Terms & <span className="gradient-text-hero">Conditions</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="terms-hero-desc"
          >
            Please read these terms carefully before using our platform.
          </motion.p>
        </div>
      </section>

      {/* Terms Content Section */}
      <section className="terms-content-section">
        <div className="container terms-container">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="terms-glass-card"
          >
            <div className="terms-header-info">
              <p><strong>Effective Date:</strong> <span className="highlight">[Effective Date]</span></p>
              <p><strong>Website/App Name:</strong> Stocks4U</p>
              <p><strong>Owned & Operated By:</strong> <span className="highlight">[Your Company Name]</span></p>
              <p><strong>Contact Email:</strong> <span className="highlight">[Your Email]</span></p>
              <p><strong>Registered Address:</strong> <span className="highlight">[Your Address]</span></p>
            </div>

            <p className="terms-intro">
              Welcome to Stocks4U. These Terms & Conditions govern your access to and use of our website, dashboard, tools, screeners, market filters, data, charts, alerts, APIs, software, content, and related services.
              <br/><br/>
              By accessing or using Stocks4U, you agree to these Terms & Conditions. If you do not agree with any part of these Terms, you must stop using the platform immediately.
            </p>

            <div className="terms-body">
              <section className="terms-point">
                <h2>1. ABOUT STOCKS4U</h2>
                <p>Stocks4U is a technology-based stock market data filtering and analysis platform.</p>
                <p>Stocks4U helps users view, organize, filter, and analyze market-related data using tools, screeners, indicators, dashboards, charts, alerts, and other technology-driven features.</p>
                <p>Stocks4U is not a stock broker, investment adviser, research analyst, portfolio manager, exchange, depository participant, mutual fund distributor, or any other regulated securities market intermediary.</p>
                <p>Stocks4U is only a third-party API/data-dependent technology platform. The platform may depend on third-party APIs, data vendors, exchanges, brokers, public sources, cloud providers, payment gateways, and other external services to display or process information.</p>
              </section>

              <section className="terms-point">
                <h2>2. THIRD-PARTY API AND DATA DEPENDENCY</h2>
                <p>Stocks4U may collect, receive, process, calculate, display, or organize data from third-party APIs, data providers, market data vendors, exchanges, brokers, public sources, or other external systems.</p>
                <p>Stocks4U does not own, control, or guarantee the accuracy, availability, completeness, speed, legality, reliability, or continuity of third-party data or APIs.</p>
                <p>Users understand that data shown on Stocks4U may be delayed, incomplete, incorrect, unavailable, interrupted, mismatched, or affected by technical errors.</p>
                <p>Stocks4U shall not be responsible for any loss, damage, trading loss, missed opportunity, wrong decision, or financial consequence caused by third-party API failure, data delay, incorrect data, server issue, internet issue, exchange issue, broker issue, calculation error, or any external dependency.</p>
                <p>Users must verify all market data from their broker, exchange terminal, official exchange website, or other reliable sources before making any trading or investment decision.</p>
              </section>

              <section className="terms-point">
                <h2>3. NO INVESTMENT ADVICE</h2>
                <p>Stocks4U does not provide investment advice, financial advice, trading tips, stock recommendations, buy/sell calls, portfolio advice, research reports, wealth management, or guaranteed profit strategies.</p>
                <p>All information available on Stocks4U is provided only for educational, informational, analytical, and technology-support purposes.</p>
                <p>Any filters, indicators, alerts, dashboards, scanners, signals, scores, watchlists, screeners, charts, or data points shown on Stocks4U are generated based on user-selected conditions, mathematical calculations, data processing, or system-based logic.</p>
                <p>Such information must not be treated as investment advice, trading advice, financial advice, or a recommendation to buy, sell, hold, enter, exit, or trade any security or financial instrument.</p>
              </section>

              <section className="terms-point">
                <h2>4. SEBI REGISTRATION DISCLAIMER</h2>
                <p>Stocks4U, its owners, directors, employees, developers, partners, affiliates, representatives, and associates are not registered with SEBI as an Investment Adviser, Research Analyst, Stock Broker, Portfolio Manager, or any other regulated securities market intermediary, unless specifically stated in writing.</p>
                <p>Stocks4U does not claim to be a SEBI-registered advisory service or research platform.</p>
                <p>Stocks4U does not provide personalized investment advice, personalized trading advice, portfolio-based advice, or individual financial recommendations.</p>
                <p>Users are fully responsible for their own trading, investing, research, analysis, risk management, and financial decisions.</p>
              </section>

              <section className="terms-point">
                <h2>5. NO BROKERAGE OR ORDER EXECUTION SERVICE</h2>
                <p>Stocks4U does not execute trades, place orders, manage user funds, hold securities, provide margin, open demat accounts, or act as a broker.</p>
                <p>If any broker API, trading API, or third-party integration is used in the future, Stocks4U will only act as a technology interface, subject to applicable laws, broker terms, API provider terms, and user authorization.</p>
                <p>Stocks4U shall not be responsible for failed orders, rejected orders, delayed execution, slippage, wrong order placement, broker downtime, exchange downtime, API failure, or any trading loss caused through third-party broker/API systems.</p>
              </section>

              <section className="terms-point">
                <h2>6. NO GUARANTEE OF DATA ACCURACY</h2>
                <p>Stocks4U tries to display accurate and useful information, but we do not guarantee that any data, alert, chart, scanner, signal, calculation, or output will be accurate, complete, updated, real-time, uninterrupted, or error-free.</p>
                <p>Market data may differ across brokers, exchanges, data vendors, and APIs.</p>
                <p>Users should not rely solely on Stocks4U for trading or investment decisions.</p>
                <p>Before taking any decision, users must independently verify the data and consult qualified professionals where required.</p>
              </section>

              <section className="terms-point">
                <h2>7. MARKET RISK DISCLAIMER</h2>
                <p>Trading and investing in stocks, futures, options, commodities, currencies, and other financial instruments involves high risk.</p>
                <p>F&O trading, intraday trading, leverage-based trading, and options trading can lead to substantial financial losses.</p>
                <p>Market movement is uncertain and may be affected by volatility, liquidity, news, global events, technical factors, regulations, and other risks.</p>
                <p>Past performance, historical data, backtesting, chart patterns, technical indicators, screeners, alerts, or previous results do not guarantee future performance.</p>
                <p>Stocks4U is not responsible for any profit, loss, missed trade, wrong trade, capital loss, opportunity loss, emotional loss, business loss, or financial damage arising from the use of the platform.</p>
              </section>

              <section className="terms-point">
                <h2>8. USER RESPONSIBILITY</h2>
                <p>By using Stocks4U, you confirm that:</p>
                <ul>
                  <li>You understand the risks of trading and investing.</li>
                  <li>You are using Stocks4U only as a technology and analysis-support tool.</li>
                  <li>You will not treat any platform output as guaranteed advice or recommendation.</li>
                  <li>You will verify data independently before making decisions.</li>
                  <li>You will make all trading and investment decisions at your own risk.</li>
                  <li>You will not hold Stocks4U responsible for your financial outcomes.</li>
                  <li>You will comply with applicable laws, exchange rules, broker terms, API terms, and platform policies.</li>
                </ul>
              </section>

              <section className="terms-point">
                <h2>9. ALERTS, SIGNALS AND SCREENERS</h2>
                <p>Stocks4U may provide alerts, signals, filters, screeners, scanners, watchlists, scores, or notifications based on predefined conditions, formulas, indicators, market data, or user-selected criteria.</p>
                <p>These alerts and signals are system-generated outputs and are not personal recommendations.</p>
                <p>Stocks4U does not guarantee that alerts will be timely, accurate, profitable, uninterrupted, or suitable for any user.</p>
                <p>Users should not enter or exit trades only because of an alert, signal, scanner, or notification shown on Stocks4U.</p>
              </section>

              <section className="terms-point">
                <h2>10. THIRD-PARTY SERVICES</h2>
                <p>Stocks4U may use or integrate with third-party services including but not limited to data APIs, brokers, exchanges, payment gateways, cloud hosting providers, analytics tools, communication tools, and external websites.</p>
                <p>Third-party services are governed by their own terms, conditions, privacy policies, pricing, and availability.</p>
                <p>Stocks4U is not responsible for any issue, loss, error, breach, delay, downtime, policy change, price change, or service interruption caused by any third-party provider.</p>
                <p>Users agree that their access to certain features may depend on the availability and permission of third-party services.</p>
              </section>

              <section className="terms-point">
                <h2>11. DATA LICENSING AND USAGE</h2>
                <p>Market data may be subject to licensing restrictions, exchange rules, data vendor terms, API provider terms, and applicable laws.</p>
                <p>Stocks4U does not grant users any ownership rights over market data, third-party data, exchange data, API data, or vendor-provided data.</p>
                <p>Users are not allowed to copy, scrape, download, redistribute, resell, republish, commercially exploit, or misuse any data available on Stocks4U unless expressly permitted in writing.</p>
                <p>If any third-party data provider, exchange, API provider, or authority restricts or stops data access, Stocks4U may modify, suspend, or remove related features without prior notice.</p>
              </section>

              <section className="terms-point">
                <h2>12. NO OFFICIAL AFFILIATION</h2>
                <p>Unless clearly stated, Stocks4U is not officially affiliated with SEBI, NSE, BSE, MCX, NSDL, CDSL, any broker, exchange, regulator, or data provider.</p>
                <p>All trademarks, names, logos, and references belonging to third parties remain the property of their respective owners.</p>
                <p>Any reference to exchanges, brokers, indices, stocks, or third-party services is only for identification, integration, display, or informational purposes.</p>
              </section>

              <section className="terms-point">
                <h2>13. ACCOUNT REGISTRATION</h2>
                <p>Some features of Stocks4U may require users to create an account.</p>
                <p>Users must provide accurate, complete, and updated information.</p>
                <p>Users are responsible for keeping their login details confidential.</p>
                <p>Any activity performed through a user account will be considered the responsibility of that user.</p>
                <p>Stocks4U may suspend or terminate any account involved in misuse, unauthorized access, policy violation, suspicious activity, abuse, fraud, or illegal activity.</p>
              </section>

              <section className="terms-point">
                <h2>14. SUBSCRIPTION, PAYMENT AND PRICING</h2>
                <p>Stocks4U may offer free and paid plans.</p>
                <p>Paid plans may provide access to premium tools, data views, filters, dashboards, alerts, or advanced features.</p>
                <p>Pricing, features, limits, and subscription plans may be changed, modified, suspended, or discontinued at any time.</p>
                <p>By purchasing a plan, users agree to pay the applicable fees.</p>
                <p>Payment failures, gateway errors, bank issues, taxes, and transaction delays may be handled according to the policies of the payment provider and Stocks4U.</p>
              </section>

              <section className="terms-point">
                <h2>15. REFUND POLICY</h2>
                <p>Unless clearly mentioned otherwise, payments made to Stocks4U are non-refundable.</p>
                <p>No refund will be provided for trading losses, market losses, dissatisfaction with results, non-usage of the platform, misunderstanding of features, or third-party API/data failure.</p>
                <p>Stocks4U may provide refunds only at its sole discretion or where required by applicable law.</p>
              </section>

              <section className="terms-point">
                <h2>16. INTELLECTUAL PROPERTY</h2>
                <p>All content, design, logo, brand name, dashboard layout, software, code, UI, UX, features, tools, filters, text, graphics, charts, and other materials available on Stocks4U are the property of Stocks4U or its licensors.</p>
                <p>Users may not copy, reproduce, modify, reverse engineer, sell, license, distribute, clone, scrape, resell, or commercially exploit any part of Stocks4U without written permission.</p>
              </section>

              <section className="terms-point">
                <h2>17. PROHIBITED ACTIVITIES</h2>
                <p>Users are strictly prohibited from:</p>
                <ul>
                  <li>Using Stocks4U for illegal or unauthorized purposes.</li>
                  <li>Copying, scraping, downloading, reselling, or redistributing data.</li>
                  <li>Reverse engineering or attempting to extract source code.</li>
                  <li>Attempting to hack, overload, damage, or disrupt the platform.</li>
                  <li>Sharing paid account access with unauthorized users.</li>
                  <li>Using bots, crawlers, automation, or scraping tools without permission.</li>
                  <li>Misrepresenting Stocks4U as a SEBI-registered advisory service.</li>
                  <li>Using Stocks4U’s name, logo, brand, or content without permission.</li>
                  <li>Uploading harmful code, malware, spam, or illegal content.</li>
                  <li>Violating any law, exchange rule, broker term, API provider term, or third-party policy.</li>
                </ul>
              </section>

              <section className="terms-point">
                <h2>18. PLATFORM AVAILABILITY</h2>
                <p>Stocks4U aims to keep the platform available, but we do not guarantee uninterrupted service.</p>
                <p>The platform may be affected by maintenance, updates, bugs, server downtime, API failure, data vendor issues, exchange issues, broker issues, internet problems, cyberattacks, or other technical reasons.</p>
                <p>Stocks4U may modify, suspend, restrict, or discontinue any feature or service at any time without prior notice.</p>
                <p>Stocks4U shall not be liable for any loss caused by temporary or permanent unavailability of the platform.</p>
              </section>

              <section className="terms-point">
                <h2>19. LIMITATION OF LIABILITY</h2>
                <p>To the maximum extent permitted by law, Stocks4U, its owners, directors, employees, developers, affiliates, partners, and representatives shall not be liable for any direct, indirect, incidental, special, consequential, financial, business, trading, or investment loss arising from:</p>
                <ul>
                  <li>Use of the platform.</li>
                  <li>Inability to use the platform.</li>
                  <li>Data errors or data delays.</li>
                  <li>API failures or third-party service issues.</li>
                  <li>Incorrect alerts, filters, or calculations.</li>
                  <li>Trading or investment decisions.</li>
                  <li>Market volatility or unexpected price movements.</li>
                  <li>Broker or exchange-related issues.</li>
                  <li>User error or misunderstanding.</li>
                  <li>Unauthorized account access.</li>
                  <li>Technical bugs or downtime.</li>
                </ul>
                <p>Your use of Stocks4U is entirely at your own risk.</p>
              </section>

              <section className="terms-point">
                <h2>20. INDEMNITY</h2>
                <p>You agree to indemnify and hold harmless Stocks4U, its owners, directors, team members, affiliates, partners, and representatives from any claims, losses, damages, liabilities, costs, penalties, or expenses arising from:</p>
                <ul>
                  <li>Your use of the platform.</li>
                  <li>Your trading or investment decisions.</li>
                  <li>Your violation of these Terms.</li>
                  <li>Your misuse of data or services.</li>
                  <li>Your violation of third-party API, broker, data vendor, or exchange terms.</li>
                  <li>Your violation of applicable laws or regulations.</li>
                </ul>
              </section>

              <section className="terms-point">
                <h2>21. PRIVACY</h2>
                <p>Use of Stocks4U is also governed by our Privacy Policy.</p>
                <p>By using Stocks4U, you agree that we may collect, store, process, and use information as described in our Privacy Policy.</p>
                <p>Users should read the Privacy Policy carefully before using the platform.</p>
              </section>

              <section className="terms-point">
                <h2>22. CHANGES TO TERMS</h2>
                <p>Stocks4U may update these Terms & Conditions at any time.</p>
                <p>Updated Terms will be posted on the website or app.</p>
                <p>Continued use of Stocks4U after changes means you accept the updated Terms.</p>
                <p>If you do not agree with the updated Terms, you must stop using the platform.</p>
              </section>

              <section className="terms-point">
                <h2>23. TERMINATION</h2>
                <p>Stocks4U may suspend, restrict, or terminate user access at any time if the user violates these Terms, misuses the platform, causes technical risk, violates laws, or creates risk for Stocks4U or other users.</p>
                <p>Upon termination, the user’s right to access the platform will immediately stop.</p>
              </section>

              <section className="terms-point">
                <h2>24. GOVERNING LAW AND JURISDICTION</h2>
                <p>These Terms & Conditions shall be governed by the laws of India.</p>
                <p>Any dispute arising out of or related to Stocks4U shall be subject to the exclusive jurisdiction of the courts located in <span className="highlight">[Your City, State, India]</span>.</p>
              </section>

              <section className="terms-point">
                <h2>25. GRIEVANCE AND CONTACT</h2>
                <p>For support, complaints, legal queries, or grievance-related communication, users may contact:</p>
                <p>Email: <span className="highlight">[Your Email]</span></p>
                <p>Company Name: <span className="highlight">[Your Company Name]</span></p>
                <p>Address: <span className="highlight">[Your Address]</span></p>
                <p>Stocks4U will try to respond within a reasonable time.</p>
              </section>

              <section className="terms-point">
                <h2>26. FINAL DISCLAIMER</h2>
                <p>Stocks4U is a third-party API/data-dependent technology platform for market data filtering and analysis.</p>
                <p>Stocks4U is not an investment adviser, research analyst, stock broker, portfolio manager, exchange, or official market data provider unless specifically stated in writing.</p>
                <p>Stocks4U does not provide tips, calls, recommendations, guaranteed profits, portfolio advice, or personalized financial guidance.</p>
                <p>All data, tools, alerts, filters, charts, and screeners are provided only for informational, educational, analytical, and technology-support purposes.</p>
                <p>Users are fully responsible for their own decisions, risks, trades, investments, capital allocation, and financial outcomes.</p>
                <p><strong>By using Stocks4U, you confirm that you have read, understood, and agreed to these Terms & Conditions.</strong></p>
              </section>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Terms;
